import type { SaleDTO } from "@easyshop/shared";
import { Pill, cx } from "@/components/ui";
import { dateFR, dayMonth, eur2, num } from "@/lib/format";

/** Un ticket : ligne résumée, détail (produits, remises, paiement) au clic. */
export function SaleRow({ s, open, onToggle, last }: { s: SaleDTO; open: boolean; onToggle: () => void; last: boolean }) {
  const discounted = s.discount > 0.005;
  const sameDay = new Date(s.at).toDateString() === new Date().toDateString();
  return (
    <div className={cx(!last && "border-b border-line-soft")}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={cx("grid w-full grid-cols-[62px_1fr_auto_18px] items-center gap-2 rounded-[10px] px-2 py-3 text-left hover:bg-page sm:grid-cols-[90px_1fr_auto_100px_18px]", open && "bg-page")}
      >
        <span className="text-[12.5px] text-muted-2">
          {!sameDay && <span className="block text-[10.5px]">{dayMonth(s.at)}</span>}
          {s.time}
        </span>
        <span className="min-w-0 truncate text-[13px] font-semibold">
          {num(s.items)} article{s.items > 1 ? "s" : ""}
          {s.lines.length > 0 && <span className="font-normal text-muted"> · {s.lines.map((l) => l.name).join(", ")}</span>}
        </span>
        <span>{discounted && <Pill className="bg-warn-bg text-warn">−{eur2(s.discount)}</Pill>}</span>
        <span className="hidden text-right text-[13px] font-bold tabular sm:block">{eur2(s.amount)}</span>
        <span aria-hidden className={cx("text-[11px] text-muted-2 transition-transform", open && "rotate-180")}>
          ▾
        </span>
      </button>
      {open && <SaleDetail s={s} />}
    </div>
  );
}

export function SaleDetail({ s, className = "mx-2 mb-3 rounded-card-sm border border-line bg-white px-4 py-3" }: { s: SaleDTO; className?: string }) {
  const gross = s.lines.reduce((t, l) => t + l.unitPrice * l.quantity, 0);
  return (
    <div className={className}>
      <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-[11.5px] text-muted-2">
        <span>
          Ticket <b className="text-ink">#{s.id}</b> · {dateFR(s.at)} à {s.time}
        </span>
        {s.payment && (
          <span>
            Paiement : <b className="text-ink">{s.payment}</b>
          </span>
        )}
        {s.customer && (
          <span>
            Client : <b className="text-ink">{s.customer}</b>
            {s.customerContact ? ` (${s.customerContact})` : ""}
          </span>
        )}
      </div>
      {s.lines.length === 0 ? (
        <div className="py-2 text-[12px] text-muted">Détail des produits pas encore synchronisé pour ce ticket.</div>
      ) : (
        <>
          {/* Téléphone : une ligne empilée par produit (le tableau déborderait). */}
          <div className="flex flex-col sm:hidden">
            {s.lines.map((l, i) => (
              <div key={i} className="border-t border-line-soft py-2 text-[12px] first:border-t-0">
                <div className="flex justify-between gap-3">
                  <span className="min-w-0 font-semibold">{l.name}</span>
                  <span className="flex-shrink-0 font-semibold tabular">{eur2(l.amount)}</span>
                </div>
                <div className="mt-[2px] flex flex-wrap gap-x-3 text-[11.5px] text-muted-2">
                  <span className="tabular">
                    {num(l.quantity)} × {eur2(l.unitPrice)}
                  </span>
                  {l.discount > 0.005 && (
                    <span className="font-semibold text-warn">
                      −{eur2(l.discount)}
                      {l.discountPct != null && ` (${l.discountPct >= 99.5 ? "offert" : `${Math.round(l.discountPct)} %`})`}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        <div className="hidden overflow-x-auto sm:block">
          <table className="w-full min-w-[460px] text-[12px]">
            <thead>
              <tr className="text-left text-[10.5px] font-bold text-muted-2">
                <th className="py-[6px] pr-2 font-bold">PRODUIT</th>
                <th className="px-2 text-right font-bold">QTÉ</th>
                <th className="px-2 text-right font-bold">PRIX U.</th>
                <th className="px-2 text-right font-bold">REMISE</th>
                <th className="pl-2 text-right font-bold">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {s.lines.map((l, i) => (
                <tr key={i} className="border-t border-line-soft">
                  <td className="py-[7px] pr-2 font-semibold">{l.name}</td>
                  <td className="px-2 text-right tabular">{num(l.quantity)}</td>
                  <td className="px-2 text-right tabular">{eur2(l.unitPrice)}</td>
                  <td className="px-2 text-right tabular">
                    {l.discount > 0.005 ? (
                      <span className="font-semibold text-warn">
                        −{eur2(l.discount)}
                        {l.discountPct != null && <span className="ml-1 text-[10.5px]">({l.discountPct >= 99.5 ? "offert" : `${Math.round(l.discountPct)} %`})</span>}
                      </span>
                    ) : (
                      <span className="text-faint">—</span>
                    )}
                  </td>
                  <td className="pl-2 text-right font-semibold tabular">{eur2(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
      <div className="mt-2 flex flex-wrap justify-end gap-x-5 gap-y-1 border-t border-line-soft pt-2 text-[12px]">
        {s.discount > 0.005 && (
          <>
            <span className="text-muted-2">
              Avant remise <b className="text-ink tabular">{eur2(gross)}</b>
            </span>
            <span className="text-warn">
              Remise <b className="tabular">−{eur2(s.discount)}</b>
            </span>
          </>
        )}
        <span>
          Total <b className="tabular">{eur2(s.amount)}</b>
        </span>
        {s.profit != null && (
          <span className="text-muted-2">
            Bénéfice <b className={cx("tabular", s.profit < 0 ? "text-danger" : "text-positive")}>{eur2(s.profit)}</b>
          </span>
        )}
      </div>
    </div>
  );
}
